import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment';
import React, { Component } from 'react';
import { Dispatch, connect } from "react-redux";
import {
    IBaseResponse, IDonorProfileAddress, IDonorProfileAddressLookupData, IDonorProfileHeaderInfo, IDonorProfileMedicalDetailsData, IDonorProfileMedicalDetailsLookupData,
    IDonorProfileMedicalDetailsPost
} from '@nkr/models/index';
import { AboASubType, AboAbSubType, MatchStatus } from '@nkr/enums/index';
import {
    IApplicationState, AppActionCreators, IAppState,
    DonorActionCreators, IDonorProfileMedicalDetailsResponse, IDonorProfileMedicalDetailsLookupDataResponse, IAmbraDirectToViewerResponse
} from '@nkr/store/index';
import { toast } from 'react-toastify';
import { IBaseDispatchProps, ToggleType } from '../models';
import Content from './content';
import { Utility } from '@nkr/services/utility';
import ConfirmHeight from './confirm-height';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void,
    onResetSaveRequest: () => void
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IDispatchProps extends IBaseDispatchProps {
    getAmbraLink: (donor: number) => Promise<IAmbraDirectToViewerResponse>,
    getMedicalDetailsData: (id: number, centerId: number) => Promise<IDonorProfileMedicalDetailsResponse>,
    postMedicalDetailsData: (id: number, centerId: number, data: IDonorProfileMedicalDetailsPost) => Promise<IBaseResponse>,
    getMedicalDetailsLookupData: (centerId: number) => Promise<IDonorProfileMedicalDetailsLookupDataResponse>,
}

interface IState {
    data: IDonorProfileMedicalDetailsData,
    oldData: IDonorProfileMedicalDetailsData,
    lookupData: IDonorProfileMedicalDetailsLookupData,
    addressData: IDonorProfileAddress,
    addressLookupData: IDonorProfileAddressLookupData,
    donorDataHash: string,
    showConfirmHeight: boolean,
    showDuplicateRecord: boolean,
    duplicateMessage: string
}

const initialState: IState = {
    data: null,
    oldData: null,
    addressData: null,
    lookupData: null,
    addressLookupData: {
        stateList: [],
        countryList: []
    },
    donorDataHash: '',
    showConfirmHeight: false,
    showDuplicateRecord: false,
    duplicateMessage: ''
}

class MedicalDetailsTab extends Component<TProps, IState> {
    readonly state: IState = initialState;

    componentDidMount = () => {
        this.getData();
    }

    componentWillReceiveProps = (nextProps: Readonly<TProps>, nextContext: any) => {
        if (this.props.saveRequested == false && nextProps.saveRequested == true) {
            this.handleValidate();
        }

        if (this.props.refreshRequested == false && nextProps.refreshRequested == true) {
            this.getData();
        }
    }

    getData = () => {
        this.props.showLoading('Getting medical details data, one moment please...').then(() => {
            Promise.all([
                this.props.getMedicalDetailsData(this.props.centerId, this.props.donorId),
                this.props.getMedicalDetailsLookupData(this.props.centerId)
            ])
                .then(([dataResponse, lookupDataResponse]) => {
                    if (dataResponse.data.success && lookupDataResponse.data.success) {
                        let data = dataResponse.data.results;

                        let { totalInches, heightFeet, heightInchesRemaining } = this.convertHeight(data.heightCm);

                        data.heightInches = totalInches;
                        data.heightFeet = heightFeet;
                        data.heightInchesRemaining = heightInchesRemaining;

                        let donorDataHash = hash(data);

                        this.setState({
                            data,
                            oldData: data,
                            donorDataHash,
                            lookupData: lookupDataResponse.data.results
                        });
                    }
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.props.hideLoading();

                    if (this.props.refreshRequested) {
                        this.props.onRefreshFinished();
                    }
                });
        });
    }

    convertHeight = (heightCm: number) => {
        let totalInches = heightCm / 2.54;
        let heightFeet = Math.floor(totalInches / 12);
        let heightInchesRemaining = Math.round(totalInches % 12);

        return { totalInches, heightFeet, heightInchesRemaining };
    }

    handleValidate = () => {
        let messages: string[] = [];

        let { data } = this.state;
        let { birthDate, creatinineClearance } = data;

        if (!birthDate) {
            messages.push('Birth date must not be blank');
        } else {
            let parsedBirthDate = moment(birthDate, 'MM/DD/YYYY', true);

            if (!parsedBirthDate.isValid()) {
                messages.push('Birth date is not valid');
            } else {
                let today = moment();
                let age = today.diff(parsedBirthDate, 'years');

                if (age < 18 || age > 100) {
                    messages.push('Birth date must be between 18 and 100 years ago');
                }
            }
        }

        if (creatinineClearance < 0 || isNaN(creatinineClearance)) {
            messages.push('Creatinine Clearance must be any number bigger than 0');
        }

        let showConfirmHeight = this.shouldConfirmHeight();

        if (messages.length == 0 && !showConfirmHeight) {
            this.handleSave();
        } else if (messages.length == 0 && showConfirmHeight) {
            this.setState({
                showConfirmHeight: true
            }, () => {
                this.props.onResetSaveRequest();
            });
        } else {
            this.props.onUpdateForErrors(ToggleType.Errors, messages);
            this.props.onResetSaveRequest();
        }
    }

    handleDataChange = (data: IDonorProfileMedicalDetailsData, checkDirty: boolean = true) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);
        let hasDataChanged = checkDirty ? newHash != this.state.donorDataHash : false;


        if (this.state.data.heightCm != data.heightCm) {
            // let totalInches = data.heightCm / 2.54;
            // let heightFeet = Math.floor(totalInches / 12);
            // let heightInchesRemaining = Math.round(totalInches % 12);

            let { totalInches, heightFeet, heightInchesRemaining } = this.convertHeight(data.heightCm);

            newData.heightInches = totalInches;
            newData.heightFeet = heightFeet;
            newData.heightInchesRemaining = heightInchesRemaining;
        }

        if (this.state.data.heightFeet != data.heightFeet || this.state.data.heightInchesRemaining != data.heightInchesRemaining) {
            let heightCm = Math.round(((data.heightFeet * 12) + data.heightInchesRemaining) * 2.54);

            newData.heightCm = heightCm;
        }

        if (this.state.data.weightKg != data.weightKg) {
            let weightLbs = Utility.roundNumber(data.weightKg * 2.20462, 0);

            newData.weightLbs = weightLbs;
        }

        if (this.state.data.weightLbs != data.weightLbs) {
            let weightKg = Utility.roundNumber(data.weightLbs * 0.453592, 0);

            newData.weightKg = weightKg;
        }

        this.setState({
            data: newData,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleSave = () => {
        let postBody: IDonorProfileMedicalDetailsPost = {
            ...this.state.data
        };

        let { donorId, centerId } = this.props;

        this.props.showLoading('Saving Medical Details, one moment pleas...').then(() => {
            this.props.postMedicalDetailsData(centerId, donorId, postBody)
                .then(res => {
                    if (res.data.success) {
                        let donorDataHash = hash(this.state.data);

                        this.setState({
                            donorDataHash,
                        }, () => {
                            this.props.onDataChanged(false);
                            this.props.onResetDataChangedWarning();
                            this.props.onUpdateForErrors(ToggleType.Success, []);
                            this.props.onSaveFinished(true);
                        });
                    }
                })
                .catch((error: any) => {
                    if (error.response.status == 409) {
                        this.setState({
                            showDuplicateRecord: true,
                            duplicateMessage: error.response.data
                        }, () => {
                            this.props.onResetSaveRequest();
                        });

                        return;
                    }

                    toast.error(error.response.data);
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    shouldConfirmHeight = () => {
        let isTooTall = this.state.data.heightCm > 213; // 7 feet tall
        let isTooShort = this.state.data.heightCm < 137; // 4 feet 6 in

        if (isTooTall || isTooShort) {
            return true;
        }

        return false;
    }

    handleConfirmHeight = () => {
        this.setState({
            showConfirmHeight: false
        }, () => {
            this.handleSave();
        });
    }

    handleCancelConfirmHeight = () => {
        this.setState({
            showConfirmHeight: false
        });
    }

    handleCloseDuplicateRecord = () => {
        this.setState({
            showDuplicateRecord: false
        });
    }

    render = () => {
        let { data, lookupData } = this.state;

        if (data == null || lookupData == null || this.props.headerInfo == null) {
            return null;
        }

        return (
            <>
                <Content
                    isSuperAdmin={this.props.isAdmin}
                    centerId={this.props.centerId}
                    data={data}
                    lookupData={lookupData}
                    isInMatch={this.props.headerInfo.statusId > MatchStatus.NeedsMatch}
                    onChange={this.handleDataChange}
                    onSaveChange={this.handleSave}
                    onGetAmbraLink={this.props.getAmbraLink}
                />

                <ConfirmHeight
                    show={this.state.showConfirmHeight}
                    heightFeet={this.state.data.heightFeet}
                    heightInchesRemaining={this.state.data.heightInchesRemaining}
                    heightCm={this.state.data.heightCm}
                    onConfirm={this.handleConfirmHeight}
                    onCancel={this.handleCancelConfirmHeight}
                />

                <DuplicateRecordModal
                    show={this.state.showDuplicateRecord}
                    message={this.state.duplicateMessage}
                    onClose={this.handleCloseDuplicateRecord}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getMedicalDetailsData: (centerId, id) => {
            return dispatch(DonorActionCreators.getProfileMedicalDetailsData(id, centerId));
        },
        postMedicalDetailsData: (centerId, id, data) => {
            return dispatch(DonorActionCreators.postProfileMedicalDetailsData(id, centerId, data));
        },
        getMedicalDetailsLookupData: (centerId) => {
            return dispatch(DonorActionCreators.getProfileMedicalDetailsLookupData(centerId));
        },
        getAmbraLink: (donorId) => {
            return dispatch(DonorActionCreators.getAmbraLink(donorId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(MedicalDetailsTab);